import { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Button, NexSays, Textarea, useToast } from '@nexply/ui';
import { Mic, Square, Sparkles } from 'lucide-react';
import { ApiError } from '@nexply/api-client';
import type { ExtractedMember, MemberExtractInput } from '@nexply/api-client';
import { api } from '../lib/api.js';

// --- Web Speech API (kein npm-Paket, nur was der Browser mitbringt) ---

interface SpeechResultAlt { transcript: string }
interface SpeechResult { isFinal: boolean; 0: SpeechResultAlt; length: number }
interface SpeechResultList { length: number; [i: number]: SpeechResult }
interface SpeechEvent { resultIndex: number; results: SpeechResultList }
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: SpeechEvent) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
}
type SpeechCtor = new () => SpeechRecognitionLike;

function getSpeechCtor(): SpeechCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechCtor;
    webkitSpeechRecognition?: SpeechCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

function clock(secs: number): string {
  return `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
}

/** Live-Auswertung: erst nach dieser Sprechpause und so viel neuem Text. */
const LIVE_PAUSE_MS = 1500;
const LIVE_MIN_NEW_CHARS = 25;

export interface ContactVoiceNoteProps {
  /** Wird false, sobald das Sheet zu ist — dann muss das Mikrofon sofort loslassen. */
  active: boolean;
  known: MemberExtractInput['known'];
  /** Bereits übernommene KI-Antwort — die Karte zeigt dann den Erfolgs-Zustand. */
  applied: boolean;
  /**
   * Schon während des Sprechens auswerten: nach jeder kurzen Sprechpause wandert
   * das bisher Gesagte zur KI und die Felder füllen sich mit. Der Knopf bleibt —
   * wer lieber erst fertig erzählt, drückt ihn am Ende.
   */
  live?: boolean;
  onExtracted: (extracted: ExtractedMember) => void;
}

export function ContactVoiceNote({
  active,
  known,
  applied,
  live = false,
  onExtracted,
}: ContactVoiceNoteProps) {
  const toast = useToast();

  const [supported] = useState(() => getSpeechCtor() !== null);
  const [recording, setRecording] = useState(false);
  const [interim, setInterim] = useState('');
  const [seconds, setSeconds] = useState(0);
  const [transcript, setTranscript] = useState('');
  const [pending, setPending] = useState(false);
  const [aiOff, setAiOff] = useState(false);
  /** Warum das Mikrofon nicht läuft — steht sichtbar in der Karte, nicht als Toast. */
  const [micError, setMicError] = useState<string | null>(null);

  const recRef = useRef<SpeechRecognitionLike | null>(null);

  const stopRecording = useCallback(() => {
    recRef.current?.stop();
    recRef.current = null;
    setRecording(false);
    setInterim('');
  }, []);

  useEffect(() => () => recRef.current?.abort(), []);

  // Sheet zu (Abbruch, Speichern) → Aufnahme darf nicht weiterlaufen.
  useEffect(() => {
    if (!active && recRef.current) {
      recRef.current.abort();
      recRef.current = null;
      setRecording(false);
      setInterim('');
    }
  }, [active]);

  useEffect(() => {
    if (!recording) return;
    setSeconds(0);
    const t = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [recording]);

  /**
   * Mikrofon-Freigabe ausdrücklich anfragen.
   *
   * Die Web Speech API fragt je nach Browser/Kontext NICHT von sich aus — dann
   * passiert nach dem Klick schlicht gar nichts, kein Dialog, kein Fehler.
   * `getUserMedia` löst den Berechtigungsdialog zuverlässig aus und liefert im
   * Fehlerfall einen benennbaren Grund. Der Stream wird sofort wieder
   * geschlossen; wir wollten nur die Freigabe, aufgenommen wird über die
   * Spracherkennung.
   */
  async function ensureMicPermission(): Promise<boolean> {
    if (!navigator.mediaDevices?.getUserMedia) {
      setMicError(
        'Dieser Browser gibt kein Mikrofon frei. Öffne die Seite über https oder localhost in Chrome, Edge oder Safari.',
      );
      return false;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      for (const track of stream.getTracks()) track.stop();
      return true;
    } catch (err) {
      const name = (err as { name?: string }).name;
      setMicError(
        name === 'NotAllowedError' || name === 'SecurityError'
          ? 'Der Mikrofon-Zugriff ist blockiert. Klick im Browser links neben die Adresse (Schloss-Symbol) und erlaube das Mikrofon für diese Seite.'
          : name === 'NotFoundError' || name === 'DevicesNotFoundError'
            ? 'Der Browser findet kein Mikrofon. Prüf, ob eins angeschlossen und im System aktiv ist.'
            : 'Das Mikrofon ließ sich nicht öffnen. Nutzt es gerade ein anderes Programm?',
      );
      return false;
    }
  }

  async function startRecording() {
    setMicError(null);

    // Ohne sicheren Kontext (http auf einer LAN-IP) sperrt der Browser Mikrofon
    // und Spracherkennung kommentarlos — das ist die häufigste stille Ursache.
    if (!window.isSecureContext) {
      setMicError(
        'Spracheingabe geht nur über https oder localhost. Über eine http-Adresse im Netzwerk sperrt der Browser das Mikrofon.',
      );
      return;
    }

    const Ctor = getSpeechCtor();
    if (!Ctor) {
      setMicError('Dieser Browser kann keine Spracherkennung. In Chrome, Edge oder Safari geht es.');
      return;
    }

    if (!(await ensureMicPermission())) return;

    const rec = new Ctor();
    rec.lang = 'de-DE';
    rec.continuous = true;
    rec.interimResults = true;

    rec.onresult = (e) => {
      let live = '';
      let final = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (!r) continue;
        if (r.isFinal) final += r[0].transcript;
        else live += r[0].transcript;
      }
      if (final) {
        setTranscript((prev) => (prev.trim() ? `${prev.trim()} ${final.trim()}` : final.trim()));
      }
      setInterim(live);
    };
    rec.onerror = (e) => {
      // Sichtbar in der Karte, nicht nur als Toast: Ein Toast ist weg, bevor man
      // versteht, warum das Mikrofon nichts tut.
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
        setMicError(
          'Der Browser hat die Spracherkennung blockiert. Erlaube das Mikrofon für diese Seite (Schloss-Symbol in der Adresszeile).',
        );
      } else if (e.error === 'network') {
        setMicError(
          'Die Spracherkennung des Browsers ist nicht erreichbar (sie läuft über einen Google-Dienst). Ohne Internet oder in Brave ist sie oft abgeschaltet — tipp den Text unten einfach ein.',
        );
      } else if (e.error === 'audio-capture') {
        setMicError('Kein Mikrofon-Signal. Prüf, ob das richtige Gerät ausgewählt ist.');
      } else if (e.error !== 'aborted' && e.error !== 'no-speech') {
        setMicError('Spracheingabe fehlgeschlagen. Versuch es erneut.');
      }
      stopRecording();
    };
    rec.onend = () => {
      recRef.current = null;
      setRecording(false);
      setInterim('');
    };

    recRef.current = rec;
    setInterim('');
    try {
      rec.start();
      setRecording(true);
    } catch (err) {
      // start() wirft synchron, z.B. wenn schon eine Erkennung läuft. Ohne
      // dieses catch blieb die Karte im Aufnahme-Zustand stehen, obwohl nichts lief.
      console.error('[Sprachnotiz] start() fehlgeschlagen:', err);
      recRef.current = null;
      setRecording(false);
      setMicError('Die Spracherkennung ließ sich nicht starten. Lade die Seite neu und probier es nochmal.');
    }
  }

  async function extract() {
    const text = transcript.trim();
    if (text.length < 3) return;
    if (recording) stopRecording();
    await runExtract(text);
  }

  // Die eigentliche Auswertung, geteilt von Knopf und Live-Modus.
  const runExtract = useCallback(
    async (text: string) => {
      setPending(true);
      try {
        const { extracted } = await api.members.extract({ transcript: text, known });
        setAiOff(false);
        onExtracted(extracted);
      } catch (err) {
        if (err instanceof ApiError && err.status === 503) {
          setAiOff(true);
        } else {
          toast.error('Nex konnte den Text nicht auswerten. Versuch es nochmal.');
        }
      } finally {
        setPending(false);
      }
    },
    // known/onExtracted kommen bei jedem Render neu — die Refs unten halten den
    // Live-Timer stabil, ohne dass er bei jedem Tastendruck neu aufgesetzt wird.
    [known, onExtracted, toast],
  );

  // --- Live-Auswertung während des Sprechens ---
  const runRef = useRef(runExtract);
  runRef.current = runExtract;
  const pendingRef = useRef(false);
  pendingRef.current = pending;
  /** Länge des Textes, der zuletzt an die KI ging — verhindert Doppel-Aufrufe. */
  const lastSentLen = useRef(0);

  useEffect(() => {
    if (!live || !recording || aiOff) return;
    const text = transcript.trim();
    if (text.length < 12) return;
    if (text.length - lastSentLen.current < LIVE_MIN_NEW_CHARS) return;

    // Erst nach einer Sprechpause losschicken: mitten im Satz wäre die Hälfte
    // der Information noch gar nicht gesagt.
    const timer = setTimeout(() => {
      if (pendingRef.current) return; // Ein Aufruf läuft noch — der nächste Tick kommt.
      lastSentLen.current = text.length;
      void runRef.current(text);
    }, LIVE_PAUSE_MS);
    return () => clearTimeout(timer);
  }, [live, recording, aiOff, transcript]);

  // Neue Aufnahme = neue Zählung.
  useEffect(() => {
    if (!active) lastSentLen.current = 0;
  }, [active]);

  const ready = transcript.trim().length >= 3;

  return (
    <div className="rounded-2xl border border-line bg-raise p-4 space-y-4">
      <div className="text-center">
        <div className="text-sm font-medium text-text">Sprachnotiz</div>
        <p className="mt-0.5 text-xs text-text-dim">
          {recording
            ? live
              ? 'Ich höre zu und fülle die Felder schon während du sprichst.'
              : 'Ich höre zu — sprich ganz normal.'
            : 'Erzähl mir, was du über die Person weißt. Nex füllt daraus die Felder.'}
        </p>
      </div>

      {supported ? (
        <div className="flex flex-col items-center gap-2">
          <div className="relative grid place-items-center">
            {recording && (
              <motion.span
                aria-hidden
                className="absolute h-24 w-24 rounded-full border-2 border-danger"
                initial={{ scale: 1, opacity: 0.6 }}
                animate={{ scale: 1.5, opacity: 0 }}
                transition={{ duration: 1.4, repeat: Infinity, ease: 'easeOut' }}
              />
            )}
            <motion.button
              type="button"
              onClick={() => (recording ? stopRecording() : void startRecording())}
              aria-label={recording ? 'Aufnahme stoppen' : 'Sprachnotiz aufnehmen'}
              animate={recording ? { scale: [1, 1.06, 1] } : { scale: 1 }}
              transition={recording ? { duration: 1.4, repeat: Infinity } : { duration: 0.2 }}
              className={
                recording
                  ? 'relative grid h-24 w-24 place-items-center rounded-full border-2 border-danger bg-danger/15 text-danger shadow-lg active:scale-95 transition-colors'
                  : 'relative grid h-24 w-24 place-items-center rounded-full border-2 border-gold/40 bg-gold/10 text-gold shadow-lg active:scale-95 transition-colors'
              }
            >
              {recording ? (
                <Square className="h-8 w-8" strokeWidth={2.5} fill="currentColor" />
              ) : (
                <Mic className="h-9 w-9" strokeWidth={2} />
              )}
            </motion.button>
          </div>

          {recording ? (
            <div className="flex items-center gap-2 text-xs font-medium text-danger">
              <span className="h-2 w-2 animate-pulse rounded-full bg-danger" />
              {clock(seconds)} — tippen zum Stoppen
            </div>
          ) : (
            <div className="text-xs text-text-muted">
              {transcript.trim() ? 'Nochmal aufnehmen — wird angehängt.' : 'Antippen und lossprechen.'}
            </div>
          )}

          {recording && (
            <div className="min-h-[44px] w-full rounded-xl border border-danger/30 bg-danger/[0.06] px-3 py-2 text-sm text-text-dim">
              {transcript.trim() && <span className="text-text">{transcript.trim()} </span>}
              {interim || (!transcript.trim() && <span className="text-text-muted">…</span>)}
            </div>
          )}

          {live && recording && (
            <div className="flex items-center gap-1.5 text-[11px] text-gold-light">
              <Sparkles className={pending ? 'h-3.5 w-3.5 animate-pulse' : 'h-3.5 w-3.5'} />
              {pending ? 'Nex sortiert das Gesagte ein …' : 'Nex füllt live mit — einfach weitererzählen.'}
            </div>
          )}
        </div>
      ) : (
        <p className="rounded-xl border border-line bg-surface-2 p-3 text-xs text-text-muted">
          Dein Browser kann keine Spracheingabe (z.&nbsp;B. Firefox). In Chrome, Edge oder Safari
          geht der Aufnahme-Knopf. Schreib es einfach unten hin — Nex wertet den getippten Text
          genauso aus.
        </p>
      )}

      {micError && (
        <p className="rounded-xl border border-warn/30 bg-warn/10 p-3 text-xs text-text-dim">
          {micError}
        </p>
      )}

      <div>
        <Textarea
          value={transcript}
          onChange={(e) => {
            setTranscript(e.currentTarget.value);
            // Neuer Text = neuer Versuch — ein früherer KI-Ausfall darf den
            // Auswerten-Knopf nicht dauerhaft ersetzen.
            if (aiOff) setAiOff(false);
          }}
          maxLength={8000}
          className="min-h-[92px] text-sm"
          placeholder="z.B. „Thomas kenne ich vom Bau-Netzwerk, er macht Trockenbau in Köln und sucht Bauträger. Arbeitet oft mit Anna Meier zusammen.“"
        />
        <p className="mt-1 text-[11px] text-text-muted">
          Spracherkennung macht Fehler — korrigier den Text ruhig, bevor Nex ihn auswertet.
          {transcript.length > 7000 && ` (${transcript.length}/8000 Zeichen)`}
        </p>
      </div>

      {aiOff ? (
        <div className="space-y-2">
          <NexSays degraded title="Die KI ist hier nicht eingerichtet">
            Auf diesem Server ist kein Anthropic-API-Schlüssel hinterlegt (ANTHROPIC_API_KEY) —
            deshalb kann ich den Text nicht auswerten. Nochmal drücken ändert daran nichts: Trag
            die Felder unten von Hand ein, oder hinterleg den Schlüssel.
          </NexSays>
          <Button fullWidth variant="secondary" disabled={!ready || recording} onClick={() => void extract()}>
            <Sparkles className="h-4 w-4" /> Nochmal versuchen
          </Button>
        </div>
      ) : pending && !(live && recording) ? (
        // Während der Live-Aufnahme reicht die schmale Zeile oben — ein großer
        // Denk-Block würde bei jeder Sprechpause aufpoppen.
        <NexSays thinking title="Nex sortiert …">
          Ich lese deine Notiz und ordne sie den Feldern zu. Verbindungen zu Leuten, die schon in
          deinem Netzwerk sind, schlage ich dir gleich vor.
        </NexSays>
      ) : (
        <Button fullWidth disabled={!ready || recording} onClick={() => void extract()}>
          <Sparkles className="h-4 w-4" />
          {applied ? 'Nochmal auswerten lassen' : 'Felder ausfüllen lassen'}
        </Button>
      )}
    </div>
  );
}
